import { IconButton } from "@mui/material";
import classNames from "classnames";
import { useEffect, useRef, useState, type FC } from "react";
import { AppIconButton } from "../../../../../shared/components/AppIconButton";
import { CopyIcon } from "../../../../../shared/icons/CopyIcon";
import { DotsIcon } from "../../../../../shared/icons/DotsIcon";
import { TrashIcon } from "../../../../../shared/icons/TrashIcon";
import styles from "../../../styles.module.css";
import { TransferObjectButton } from "../../SidebarButtons/TransferObjectButton";
import { LocalDatabaseIcon } from "../../../../../shared/icons/LocalDatabaseIcon";
import { CloudIcon } from "../../../../../shared/icons/CloudIcon";
import { StorageScope } from "../../../../../services/api/instances/storageApi/types/service_types";
import { AppTooltip } from "../../../../../shared/components/AppTooltip";

interface Props {
  onSelect: (id: string) => void;
  onOpenModal?: (id: string) => void;
  selectedId?: string | null;
  itemName?: string;
  onDelete?: (id: string, name: string) => void;
  isSendDisabled?: boolean;
  className?: string;
  isTransfer?: boolean;
  onOpenMenu?: (event: React.MouseEvent<HTMLElement>) => void;
  onCopyObject?: (id: string) => void;
  hasMenu?: boolean;
  service?: StorageScope;
  iconTagType?: string;
}

export const SidebarItem: FC<Props> = ({
  onSelect,
  selectedId,
  itemName,
  onDelete,
  isSendDisabled,
  onOpenModal,
  isTransfer,
  onOpenMenu,
  hasMenu,
  onCopyObject,
  className,
  service,
  iconTagType,
}) => {
  const [isLongItemName, setIsLongItemName] = useState<boolean>(false);
  const textRef = useRef<HTMLLIElement>(null);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, clientWidth } = textRef.current;
      setIsLongItemName(scrollWidth > clientWidth);
    }
  }, [itemName]);

  const renderIcon = () => {
    if (iconTagType === "local") {
      return (
        <LocalDatabaseIcon
          className={styles["icon-tag"]}
          width={16}
          height={16}
        />
      );
    }
    if (iconTagType === "openai") {
      return (
        <CloudIcon className={styles["icon-tag"]} width={16} height={16} />
      );
    }
    return null;
  };

  return (
    <li
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={className}
    >
      {iconTagType && renderIcon()}
      <AppTooltip
        classes={{ tooltip: styles["tooltip"] }}
        title={itemName}
        open={isHovering && isLongItemName}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [-10, 5],
                },
              },
            ],
          },
        }}
      >
        <span
          ref={textRef}
          className={classNames(styles["item-name"], {
            [styles.disabled]: isSendDisabled,
          })}
          onClick={() => onSelect(selectedId as string)}
        >
          {itemName}
        </span>
      </AppTooltip>
      {hasMenu ? (
        <AppIconButton onClick={onOpenMenu} className={styles["menu-button"]}>
          <DotsIcon />
        </AppIconButton>
      ) : (
        <div className={styles["actions-container"]}>
          {isTransfer && (
            <TransferObjectButton
              handleClick={() => onOpenModal?.(selectedId as string)}
              className={styles["transfer-modal-open"]}
            />
          )}
          {onCopyObject && (
            <AppIconButton
              onClick={() => onCopyObject(selectedId as string)}
              className={styles["copy-button"]}
            >
              <CopyIcon />
            </AppIconButton>
          )}
          {onDelete && (
            <IconButton
              className={classNames(styles["delete-button"], {
                [styles.disabled]: isSendDisabled,
              })}
              onClick={() => onDelete(selectedId as string, itemName as string)}
            >
              <TrashIcon />
            </IconButton>
          )}
        </div>
      )}
    </li>
  );
};
